import React, { useState, useMemo } from 'react';
import {
  Avatar,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import { usePriceFormatter } from '../../../hooks';
import { renderPaymentIcon } from '../../../utils/utils';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import { useAppSelector } from '../../../Redux/hooks';

interface ActiveLoansProps {
  boosterLoanHistoryData: any;
}

const HistoryTable: React.FC<ActiveLoansProps> = ({ boosterLoanHistoryData }) => {
  const { paymentMethods } = usePaymentMethods();
  const navigation = useNavigate();
  const formatter = usePriceFormatter();
  const currencyInfo = useAppSelector((state) => state.currency);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const safeDateFormat = (timestamp: any, format: string = 'DD-MMM-YYYY'): string => {
    if (!timestamp) return 'N/A';
    try {
      return moment(+timestamp).format(format);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getLogoUrl = (symbol: string) => {
    if (!symbol) {
      return '';
    }
    return `https://logos-api.extend.apexclearing.com/logos/${symbol.toLowerCase().trim()}.png?api_key=4RJeGywzAE&api_secret=isUE1ZzPNi5IzXbs9Qg0S01eaFUcxX09c4U88R9JBiJgJ6WxXHrXz3VtEima`;
  };

  const navigateToDetail = async (data: any) => {
    navigation('/boosterLoanDetails', {
      state: {
        symbol: data.symbol,
        title: data.symbol,
        loanDetails: data,
        icon: getLogoUrl(data.symbol),
        loan_id: data.id || data.loan_id,
      },
    });
  };

  // Get status chip color and styling
  const getStatusChipProps = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';

    if (statusUpper === 'PAID' || statusUpper === 'SUCCESS') {
      return {
        backgroundColor: '#4EDF6D33',
        color: '#4EDF6D',
        label: statusUpper === 'PAID' ? 'PAID' : 'Success'
      };
    } else if (statusUpper === 'FAILED') {
      return {
        backgroundColor: '#FF2A2A33',
        color: '#FF2A2A',
        label: 'Failed'
      };
    } else {
      return {
        backgroundColor: '#6B728033',
        color: '#9CA3AF',
        label: status || 'Unknown'
      };
    }
  };

  // Define columns for CommonDataTable
  const columns = useMemo(() => [
    {
      name: 'Company',
      selector: (row: any) => row.symbol || '',
      cell: (row: any) => (
                  <Box display="flex" alignItems="center">
                    <Avatar
                      src={row.logoUrl ? row.logoUrl : getLogoUrl(row.symbol)}
                      alt={row?.symbol || 'Company'}
                      style={{ marginRight: '8px', height: '36px', width: '36px' }}
                    />
                    <Typography style={{ fontSize: '14px', color: 'white' }}>
                      {row?.stockName || row?.symbol || 'Unknown Company'}
                    </Typography>
                  </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Total Instalments',
      selector: (row: any) => row.total_installment || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
                  {row.total_installment || 'N/A'}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Day & Date',
      selector: (row: any) => row.last_payment_date || '',
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
          {safeDateFormat(row.last_payment_date, 'MMM DD, YYYY')} |{' '}
          <span style={{ color: '#4A7AF2' }}>{safeDateFormat(row.last_payment_date, 'dddd')}</span>
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '220px',
    },
    {
      name: 'Amount',
      selector: (row: any) => row.paid_amount || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', textAlign: 'end' }}>
                  {formatter.balance(row.paid_amount).display}
        </Typography>
      ),
      sortable: true,
      right: true,
      grow: 1,
      minWidth: '120px',
    },
    {
      name: 'Payment Method',
      selector: (row: any) => row.payment_method_id || '',
      cell: (row: any) => {
        const method = paymentMethods?.find(
          (m: any) => m.id === row.payment_method_id
        );

        if (!method) {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Typography style={{ color: "white" }}>No method</Typography>
            </Box>
          );
        }

        const icon = renderPaymentIcon(method, 28);
        const details = method.type === 'shareSellDown' || method.type === 'bankAccount'
          ? method.displayName
          : `${method.displayName} ${method.displayNumber}`;

        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon}
            <Typography style={{ color: "white", fontSize: '14px' }}>{details}</Typography>
          </Box>
        );
      },
      sortable: false,
      center: true,
      grow: 2,
      minWidth: '220px',
    },
    {
      name: 'Status',
      selector: (row: any) => row.status || '',
      cell: (row: any) => {
        const statusProps = getStatusChipProps(row.status);
        return (
                  <Chip
                    label={statusProps.label}
                    style={{
                      fontSize: '14px',
                      backgroundColor: statusProps.backgroundColor,
                      color: statusProps.color,
                      padding: '5px 10px',
                      minWidth: '80px'
                    }}
                  />
        );
      },
      sortable: true,
      center: true,
      grow: 1,
      minWidth: '120px',
    },
  ], [paymentMethods, formatter, currencyInfo]);

  return (
    <div style={{
      marginTop: '30px',
      color: 'white',
      background: 'transparent',
      borderRadius: '20px',
      border: '2px solid #FFFFFF1A',
      padding: '20px',
      width: '100%',
      overflow: 'hidden'
    }}>
      <CommonDataTable
        data={boosterLoanHistoryData || []}
        totalRows={boosterLoanHistoryData?.length || 0}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        columns={columns}
        title="Loan History"
        pagination={false}
        rowNavigateTo={(row: any) => ({
          url: '/boosterLoanDetails',
          state: {
            symbol: row.symbol,
            title: row.symbol,
            loanDetails: row,
            icon: getLogoUrl(row.symbol),
            loan_id: row.id || row.loan_id,
          }
        })}
        customStyles={{
          table: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          tableWrapper: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          headCells: {
            style: {
              fontSize: "12px",
              color: "white",
              fontWeight: "600",
              backgroundColor: "transparent",
              borderBottom: "1px solid #FFFFFF1A",
              paddingBottom: "12px",
              paddingTop: "16px",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
          cells: {
            style: {
              color: "white",
              fontSize: '16px',
              border: "none",
              padding: "12px 8px",
              backgroundColor: "transparent",
            },
          },
          rows: {
            style: {
              borderColor: "transparent !important",
              backgroundColor: 'transparent',
              width: '98% !important',
              borderRadius: "8px",
              margin: '2px 8px',
              padding: "4px",
              transition: "all 0.3s ease",
              cursor: 'pointer',
              outline: "none !important",
              '&:hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                outline: "none !important",
              },
              '&:nth-child(even)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(odd)': {
                backgroundColor: 'transparent',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(even):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
              '&:nth-child(odd):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
            },
          },
        }}
      />
    </div>
  );
};

export default HistoryTable;